import { CONFIG } from 'constants/index';
import i18n from 'i18n';
import { CatalogFilter, CatalogItem, FilterItem } from 'models';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ServiceRequestActions from 'redux/actions/serviceRequestAction';
import { RootState } from 'redux/store';
import { CatalogServices } from 'services';
import { scrollToTop } from 'utils/function';
import { usePermission } from './usePermission';
import { useUserData } from './useUserData';

interface IcDataSR {
  data: CatalogItem[],
  totalCount: number
};

interface FilterItemCatalog extends FilterItem {
  id: string;
  parent_id: string;
}

interface SelectedFilter {
  parent: { [key: string]: FilterItemCatalog[]; }
  child: { [key: string]: FilterItemCatalog[]; }
}

const FILTER_KEY = 'category';
const PAGE_SIZE = 10;

export const mapFilters = (list: any[]): any[] => {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((item: any) => ({
    label: item?.title || item?.label,
    id: item?.id,
    parent_id: item?.parent_id || '',
    value: item?.id,
    selected: false,
    count: item?.totalCount,
    childs: mapFilters(item?.children || item?.childs),
    keyName: item?.id,
  }));
};

const useAutomatedServiceRequest = () => {
  const [loading, setLoading] = useState(false);
  const [listApp, setListApp] = useState<IcDataSR>({ data: [], totalCount: 0 });
  const [listFilter, setListFilter] = useState<CatalogFilter[]>([]);
  const { t } = useTranslation(['common']);
  const userData = useUserData();
  const dispatch = useDispatch();
  const { generalConfig } = useSelector((state: RootState) => state.configReducer);
  const { listCatalog, loading: loadingServiceRequest } = useSelector((state: RootState) => state.serviceRequestReducer);
  const params = useParams() as { title : string};
  const { enabled: isAutomatedServiceRequestEnabled } = usePermission({
    type: 'permission',
    keys: [CONFIG.PERMISSION_KEY.AUTOMATED_SERVICE_REQUEST],
  });
  const [selectedFilter, setSelectedFilter] = useState<SelectedFilter>({
    parent: {},
    child: {},
  });
  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: PAGE_SIZE });

  const isEnabled = userData?.email && generalConfig?.uptimeApimServices && isAutomatedServiceRequestEnabled;

  const matchedCatalog = useMemo(() => {
    if (!params?.title) return;
    return listCatalog.find(item => item.title === params.title);
  }, [listCatalog, params?.title]);

  const dataFilter: FilterItem[] = useMemo(() => {
    return [
      {
        label: t(`common:${FILTER_KEY}`),
        keyName: FILTER_KEY,
        childs: mapFilters(listFilter),
        multiple: true,
      },
    ];
  }, [listFilter, i18n.language]);

  const dataFilterChild = useMemo(() => {
    return Object.values(selectedFilter.parent).flat().filter(item => item.childs?.length > 0);
  }, [dataFilter, JSON.stringify(selectedFilter.parent)]);

  const onPageChange = (page: number) => {
    setPagination(prev => ({ ...prev, pageIndex: page }));
    scrollToTop();
  };

  const onFilterChange = (type: 'parent' | 'child') => (value: FilterItem[], key: string) => {
    const newSelectedFilter = {
      ...selectedFilter,
      [type]: {
        ...selectedFilter[type],
        [key]: value,
      },
    };

    const parentIds = newSelectedFilter.parent?.[FILTER_KEY]?.map(item => item.id);
    Object.keys(newSelectedFilter.child).forEach(key => {
      if (!parentIds.includes(key)) {
        delete newSelectedFilter.child[key];
      }
    });

    setSelectedFilter(newSelectedFilter);
    onPageChange(1);
  };

  useEffect(() => {
    if (!isEnabled || !matchedCatalog) return;

    const parentIds = new Set(selectedFilter.parent?.[FILTER_KEY]?.map((item) => item.id));
    const childIds = Object.values(selectedFilter.child)?.map((item) => {
      if (parentIds.has(item[0]?.parent_id)) parentIds.delete(item[0]?.parent_id);
      return item[0]?.id;
    })

    const categoryIds = [...parentIds, ...childIds];
    const joinedCategoryIds = categoryIds.length > 0 ? categoryIds.join(',') : undefined;

    const payload = {
      category_ids: joinedCategoryIds,
      catalog_ids: matchedCatalog.id || '',
      page_size: pagination.pageSize,
      page_index: pagination.pageIndex,
    };

    (async () => {
      setLoading(true);
      try {
        const res = await CatalogServices.getListSRSetting(payload);
        if (res) {
          setListApp(res);
        }
      } catch (error) {
        console.error('Failed to getListSRSetting:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [isEnabled, matchedCatalog, pagination, JSON.stringify(selectedFilter)]);

  useEffect(() => {
    if (!isEnabled || loadingServiceRequest) return;
    dispatch(ServiceRequestActions.getListCatalog({ active: true }));
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled || !matchedCatalog) return;

    (async () => {
      const res = await CatalogServices.getFilterCatalog(
        matchedCatalog.id,
        {},
      );
      setListFilter(res);
    })();
  }, [isEnabled, matchedCatalog]);

  useEffect(() => {
    setSelectedFilter({ parent: {}, child: {} });
  }, [matchedCatalog]);

  return useMemo(() => {
    return {
      loading: loading || loadingServiceRequest,
      listApp,
      matchedCatalog,
      selectedFilter,
      onFilterChange,
      dataFilter,
      dataFilterChild,
      pagination,
      onPageChange,
      listCatalog,
    };
  }, [loading, listApp, listCatalog, dataFilter, dataFilterChild, matchedCatalog]);
};

export default useAutomatedServiceRequest;
